/**
 * FieldChangeIndicator Component
 * Shows a small avatar badge on form fields that were recently changed by other users
 */

import { useState, useEffect, useCallback, createContext, useContext, useMemo, type ReactNode } from 'react'
import { Avatar, Tooltip, Fade, Box } from '@mui/material'
import type { FlowChange } from '@services/flowHub.service'
import { useAdventureCollaborationOptional } from '@contexts/AdventureCollaborationContext'

// Duration to show the indicator (5 seconds)
const INDICATOR_DURATION = 5000

export interface FieldChange {
  id: string
  field: string
  userId: string
  userName: string
  expiresAt: number
}

/**
 * Get initials from a name
 */
function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Generate a consistent color based on user ID
 */
function getUserColor(userId: string): string {
  const colors = [
    '#1976d2', // blue
    '#388e3c', // green
    '#f57c00', // orange
    '#7b1fa2', // purple
    '#c2185b', // pink
    '#0097a7', // cyan
    '#5d4037', // brown
    '#455a64', // blue-grey
  ]

  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

interface FieldChangeIndicatorProps {
  /** The field name to check for recent changes */
  field: string
  /** Optional offset from top-right corner */
  offset?: { top?: number; right?: number }
}

/**
 * Small avatar indicator shown on a form field when another user edits it
 * Must be used within a FieldChangeIndicatorProvider
 */
export function FieldChangeIndicator({ field, offset }: FieldChangeIndicatorProps) {
  const { getChangeForField } = useFieldChangeIndicators()
  const change = getChangeForField(field)

  const [visible, setVisible] = useState(!!change)

  useEffect(() => {
    if (!change) {
      setVisible(false)
      return
    }

    setVisible(true)
    const remaining = change.expiresAt - Date.now()
    if (remaining <= 0) {
      setVisible(false)
      return
    }

    const timer = setTimeout(() => {
      setVisible(false)
    }, remaining)

    return () => clearTimeout(timer)
  }, [change])

  if (!change || !visible) return null

  return (
    <Fade in={visible} timeout={300}>
      <Tooltip title={`${change.userName} edited this`} placement="top">
        <Avatar
          sx={{
            position: 'absolute',
            top: offset?.top ?? -8,
            right: offset?.right ?? -8,
            width: 24,
            height: 24,
            fontSize: 10,
            bgcolor: getUserColor(change.userId),
            border: '2px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 10,
            cursor: 'default',
            pointerEvents: 'auto',
            animation: 'fieldPulse 2s ease-in-out infinite',
            '@keyframes fieldPulse': {
              '0%, 100%': {
                transform: 'scale(1)',
              },
              '50%': {
                transform: 'scale(1.1)',
              },
            },
          }}
        >
          {getInitials(change.userName)}
        </Avatar>
      </Tooltip>
    </Fade>
  )
}

/**
 * Wrapper component that adds the indicator to a form field
 * Wraps the field in a relative positioned container
 */
interface WithFieldChangeIndicatorProps {
  field: string
  children: ReactNode
  /** Optional offset from top-right corner */
  offset?: { top?: number; right?: number }
}

export function WithFieldChangeIndicator({ field, children, offset }: WithFieldChangeIndicatorProps) {
  return (
    <Box sx={{ position: 'relative' }}>
      {children}
      <FieldChangeIndicator field={field} offset={offset} />
    </Box>
  )
}

// Context for field change indicators
interface FieldChangeIndicatorContextValue {
  addChange: (change: FlowChange) => void
  getChangeForField: (field: string) => FieldChange | undefined
  recentChanges: FieldChange[]
}

const FieldChangeIndicatorContext = createContext<FieldChangeIndicatorContextValue | null>(null)

/**
 * Hook to access field change indicator functionality
 */
export function useFieldChangeIndicators() {
  const context = useContext(FieldChangeIndicatorContext)
  if (!context) {
    // Return a no-op version if not within provider
    return {
      addChange: () => {},
      getChangeForField: () => undefined,
      recentChanges: [],
    }
  }
  return context
}

interface FieldChangeIndicatorProviderProps {
  children: ReactNode
}

/**
 * Provider that manages field change tracking
 * Should wrap the adventure editor
 */
export function FieldChangeIndicatorProvider({ children }: FieldChangeIndicatorProviderProps) {
  const [recentChanges, setRecentChanges] = useState<FieldChange[]>([])
  const collaboration = useAdventureCollaborationOptional()

  // Clean up expired changes periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setRecentChanges(prev => prev.filter(c => c.expiresAt > now))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const addChange = useCallback((change: FlowChange) => {
    const { changeData, userId, userName } = change

    // Only handle adventure_field_update changes
    if (changeData.type !== 'adventure_field_update') return

    const { field } = changeData as { type: 'adventure_field_update'; field: string; value: unknown }

    const fieldChange: FieldChange = {
      id: change.id,
      field,
      userId,
      userName,
      expiresAt: Date.now() + INDICATOR_DURATION,
    }

    setRecentChanges(prev => {
      // Remove any existing change for the same field to avoid duplicates
      const filtered = prev.filter(c => c.field !== field)
      return [...filtered, fieldChange]
    })
  }, [])

  // Subscribe to collaboration changes to track field edits
  useEffect(() => {
    if (!collaboration) return

    const unsubscribe = collaboration.subscribeToRemoteChanges((change) => {
      addChange(change)
    })

    return unsubscribe
  }, [collaboration, addChange])

  const getChangeForField = useCallback((field: string): FieldChange | undefined => {
    return recentChanges.find(c => c.field === field)
  }, [recentChanges])

  const value = useMemo(() => ({
    addChange,
    getChangeForField,
    recentChanges,
  }), [addChange, getChangeForField, recentChanges])

  return (
    <FieldChangeIndicatorContext.Provider value={value}>
      {children}
    </FieldChangeIndicatorContext.Provider>
  )
}
